import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Custom-domain white-label detector.
 *
 * On app boot, we ask the backend whether the page's hostname is registered as
 * a courier's active custom domain. When `isWhiteLabel === true`, the UI hides
 * Kanal-platform chrome (Landing page CTAs to register a new courier company,
 * driver-signup tile, etc.) and presents itself as that courier's own portal.
 *
 * Login on the branded host is independently locked to that company server-side
 * (see `_normalize_domain` checks in /api/auth/login), so this context is purely
 * about UI presentation — security is enforced on the backend.
 */
const BrandingContext = createContext({
  loading: true,
  isWhiteLabel: false,
  companyId: null,
  companyName: null,
  publicSlug: null,
  logoPath: null,
  country: null,
});

const HOSTS_NEVER_WHITE_LABEL = new Set([
  "localhost",
  "127.0.0.1",
  // Emergent preview / stage hosts — useful for testing the platform itself
  // without ever resolving as white-label even if a stray DB row was created.
]);

export function BrandingProvider({ children }) {
  const [state, setState] = useState({ loading: true, isWhiteLabel: false });

  useEffect(() => {
    const host = (typeof window !== "undefined" ? window.location.hostname : "") || "";
    if (!host || HOSTS_NEVER_WHITE_LABEL.has(host) || host.endsWith(".emergentagent.com") || host.endsWith(".emergent.host")) {
      setState({ loading: false, isWhiteLabel: false });
      return;
    }
    api
      .get("/public/site/by-host", { params: { host } })
      .then(({ data }) => {
        if (data?.is_white_label) {
          setState({
            loading: false,
            isWhiteLabel: true,
            companyId: data.company_id,
            companyName: data.company_name,
            publicSlug: data.public_slug,
            logoPath: data.logo_path,
            country: data.country,
            contactEmail: data.contact_email,
          });
          // Update browser tab title for a polished branded feel.
          if (data.company_name && typeof document !== "undefined") {
            document.title = data.company_name;
          }
        } else {
          setState({ loading: false, isWhiteLabel: false });
        }
      })
      .catch(() => setState({ loading: false, isWhiteLabel: false }));
  }, []);

  return <BrandingContext.Provider value={state}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  return useContext(BrandingContext);
}
